import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Download, FileText } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { CompactTable } from '../../components/ui/CompactTable';
import { TableFilterBar } from '../../components/table-filters/TableFilterBar';
import { api } from '../../services/api';
import { legacyApi, Supplier } from '../../services/legacyApi';
import { SupplierStatementDetail, SupplierStatementSummaryRow } from '../../services/types';
import { toast } from 'sonner';

const formatMoney = (value: number) =>
    (Number(value || 0)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export function SupplierStatement() {
    const [searchParams, setSearchParams] = useSearchParams();
    const [suppliers, setSuppliers] = useState<Supplier[]>([]);
    const [summaryRows, setSummaryRows] = useState<SupplierStatementSummaryRow[]>([]);
    const [summaryTotal, setSummaryTotal] = useState(0);
    const [loadingSummary, setLoadingSummary] = useState(false);
    const [detail, setDetail] = useState<SupplierStatementDetail | null>(null);
    const [loadingDetail, setLoadingDetail] = useState(false);

    const q = searchParams.get('q') || '';
    const fromDate = searchParams.get('from') || '';
    const toDate = searchParams.get('to') || '';
    const selectedSupplierId = Number(searchParams.get('supplier_id') || 0) || null;

    const setParam = (key: string, value?: string) => {
        const next = new URLSearchParams(searchParams);
        if (value && value.trim().length > 0) {
            next.set(key, value);
        } else {
            next.delete(key);
        }
        setSearchParams(next, { replace: true });
    };

    useEffect(() => {
        legacyApi.suppliers.list().then(setSuppliers).catch(() => setSuppliers([]));
    }, []);

    useEffect(() => {
        let cancelled = false;
        setLoadingSummary(true);
        api.financials
            .listSupplierStatementsSummary({
                q: q || undefined,
                from: fromDate || undefined,
                to: toDate || undefined,
                limit: 200,
                offset: 0,
            })
            .then((payload) => {
                if (cancelled) return;
                setSummaryRows(payload.items || []);
                setSummaryTotal(Number(payload.total || 0));
            })
            .catch((err) => {
                if (cancelled) return;
                setSummaryRows([]);
                setSummaryTotal(0);
                toast.error(err?.message || 'Failed to load supplier statement summary');
            })
            .finally(() => {
                if (!cancelled) setLoadingSummary(false);
            });
        return () => {
            cancelled = true;
        };
    }, [q, fromDate, toDate]);

    useEffect(() => {
        if (!selectedSupplierId) {
            setDetail(null);
            return;
        }
        let cancelled = false;
        setLoadingDetail(true);
        api.financials
            .getSupplierStatement(selectedSupplierId, {
                from: fromDate || undefined,
                to: toDate || undefined,
            })
            .then((payload) => {
                if (!cancelled) setDetail(payload);
            })
            .catch((err) => {
                if (!cancelled) {
                    setDetail(null);
                    toast.error(err?.message || 'Failed to load supplier statement');
                }
            })
            .finally(() => {
                if (!cancelled) setLoadingDetail(false);
            });

        return () => {
            cancelled = true;
        };
    }, [selectedSupplierId, fromDate, toDate]);

    const detailRows = useMemo(
        () =>
            (detail?.transactions || []).map((row, idx) => ({
                ...row,
                row_key: `${row.source_type}-${row.source_id}-${idx}`,
            })),
        [detail],
    );

    const summaryColumns = useMemo(
        () => [
            { header: 'Supplier', accessor: (r: SupplierStatementSummaryRow) => r.supplier_name, className: 'font-medium' },
            { header: 'Opening', accessor: (r: SupplierStatementSummaryRow) => formatMoney(r.opening_balance), className: 'text-right' },
            { header: 'Debits (Payments)', accessor: (r: SupplierStatementSummaryRow) => formatMoney(r.debits), className: 'text-right' },
            { header: 'Credits (Bills)', accessor: (r: SupplierStatementSummaryRow) => formatMoney(r.credits), className: 'text-right' },
            { header: 'Closing', accessor: (r: SupplierStatementSummaryRow) => formatMoney(r.closing_balance), className: 'text-right font-semibold' },
            { header: 'Last Txn', accessor: (r: SupplierStatementSummaryRow) => r.last_txn_date || '-', className: 'whitespace-nowrap' },
            {
                header: 'Action',
                accessor: (r: SupplierStatementSummaryRow) => (
                    <Button variant="outline" size="sm" onClick={() => setParam('supplier_id', String(r.supplier_id))}>
                        View
                    </Button>
                ),
                className: 'text-right',
            },
        ],
        [searchParams],
    );

    const detailColumns = useMemo(
        () => [
            { header: 'Date', accessor: (r: any) => r.txn_date || '-', className: 'whitespace-nowrap' },
            { header: 'Type', accessor: (r: any) => r.doc_type, className: 'whitespace-nowrap' },
            { header: 'Doc No', accessor: (r: any) => r.doc_no, className: 'font-mono whitespace-nowrap' },
            { header: 'Description', accessor: (r: any) => r.description, className: '' },
            { header: 'Debit (Pay)', accessor: (r: any) => formatMoney(r.debit), className: 'text-right' },
            { header: 'Credit (Bill)', accessor: (r: any) => formatMoney(r.credit), className: 'text-right' },
            { header: 'Running', accessor: (r: any) => formatMoney(r.running_balance), className: 'text-right font-semibold' },
        ],
        [],
    );

    const selectedSupplierName = detail?.supplier?.supplier_name || suppliers.find((s) => s.supplier_id === selectedSupplierId)?.supplier_name || '';

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-xl font-semibold">Supplier Statement of Accounts</h2>
                    <p className="text-sm text-muted-foreground">AP statement based on posted bills, debit notes, and payments.</p>
                </div>
                <div className="text-xs text-muted-foreground">Currency: AED</div>
            </div>

            <TableFilterBar
                searchValue={q}
                onSearchChange={(value) => setParam('q', value)}
                searchPlaceholder="Search supplier name or ID..."
                isDirty={Boolean(q || fromDate || toDate || selectedSupplierId)}
                onClear={() => {
                    const next = new URLSearchParams(searchParams);
                    next.delete('q');
                    next.delete('from');
                    next.delete('to');
                    next.delete('supplier_id');
                    setSearchParams(next, { replace: true });
                }}
                rightSlot={`${summaryTotal} suppliers`}
            />

            <div className="grid grid-cols-1 gap-3 rounded-lg border bg-card p-3 lg:grid-cols-5">
                <div className="lg:col-span-2">
                    <label className="mb-1 block text-xs font-medium text-muted-foreground">Supplier</label>
                    <select
                        className="h-10 w-full rounded-md border bg-background px-3 text-sm"
                        value={selectedSupplierId || ''}
                        onChange={(e) => setParam('supplier_id', e.target.value || undefined)}
                    >
                        <option value="">Select Supplier</option>
                        {suppliers.map((supplier) => (
                            <option key={supplier.supplier_id} value={supplier.supplier_id}>
                                {supplier.supplier_name}
                            </option>
                        ))}
                    </select>
                </div>
                <div>
                    <label className="mb-1 block text-xs font-medium text-muted-foreground">From</label>
                    <input
                        type="date"
                        className="h-10 w-full rounded-md border bg-background px-3 text-sm"
                        value={fromDate}
                        onChange={(e) => setParam('from', e.target.value || undefined)}
                    />
                </div>
                <div>
                    <label className="mb-1 block text-xs font-medium text-muted-foreground">To</label>
                    <input
                        type="date"
                        className="h-10 w-full rounded-md border bg-background px-3 text-sm"
                        value={toDate}
                        onChange={(e) => setParam('to', e.target.value || undefined)}
                    />
                </div>
                <div className="flex items-end justify-end gap-2">
                    <Button
                        variant="outline"
                        onClick={async () => {
                            if (!selectedSupplierId) {
                                toast.error('Select a supplier to export statement CSV');
                                return;
                            }
                            try {
                                await api.financials.exportSupplierStatementCsv(selectedSupplierId, {
                                    from: fromDate || undefined,
                                    to: toDate || undefined,
                                });
                            } catch (err: any) {
                                toast.error(err?.message || 'Failed to export statement CSV');
                            }
                        }}
                    >
                        <Download className="mr-2 h-4 w-4" />
                        Export CSV
                    </Button>
                </div>
            </div>

            <div className="rounded-lg border bg-card p-2">
                <div className="mb-2 flex items-center gap-2 px-1 text-sm font-medium">
                    <FileText className="h-4 w-4" />
                    Supplier Balances
                </div>
                {loadingSummary ? (
                    <div className="p-4 text-sm text-muted-foreground">Loading supplier balances...</div>
                ) : (
                    <CompactTable data={summaryRows} columns={summaryColumns} keyField="supplier_id" />
                )}
            </div>

            {selectedSupplierId && (
                <div className="space-y-3 rounded-lg border bg-card p-3">
                    <div className="flex items-center justify-between">
                        <h3 className="text-lg font-semibold">Statement Detail{selectedSupplierName ? `: ${selectedSupplierName}` : ''}</h3>
                        <div className="text-xs text-muted-foreground">
                            Period: {fromDate || 'Beginning'} to {toDate || 'Today'}
                        </div>
                    </div>

                    {loadingDetail ? (
                        <div className="p-4 text-sm text-muted-foreground">Loading statement detail...</div>
                    ) : detail ? (
                        <>
                            <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                                <div className="rounded border p-3">
                                    <div className="text-xs text-muted-foreground">Opening B/F</div>
                                    <div className="text-lg font-semibold">{formatMoney(detail.opening_balance)}</div>
                                </div>
                                <div className="rounded border p-3">
                                    <div className="text-xs text-muted-foreground">Debits (Payments)</div>
                                    <div className="text-lg font-semibold text-emerald-700">{formatMoney(detail.debits_total)}</div>
                                </div>
                                <div className="rounded border p-3">
                                    <div className="text-xs text-muted-foreground">Credits (Bills)</div>
                                    <div className="text-lg font-semibold text-blue-700">{formatMoney(detail.credits_total)}</div>
                                </div>
                                <div className="rounded border p-3">
                                    <div className="text-xs text-muted-foreground">Closing Balance</div>
                                    <div className="text-lg font-semibold">{formatMoney(detail.closing_balance)}</div>
                                </div>
                            </div>

                            <CompactTable data={detailRows} columns={detailColumns} keyField="row_key" />
                        </>
                    ) : (
                        <div className="p-4 text-sm text-muted-foreground">No statement data available for this supplier.</div>
                    )}
                </div>
            )}
        </div>
    );
}
